import { FC, useEffect, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../app/hooks'
import { addItem } from '../app/cartSlice'
import { Button } from '../components/ui/Button'
import { Card } from '../components/ui/Card'
import { Panel } from '../components/ui/Panel'
import { PageTitle } from '../components/ui/PageTitle'
import { PageHeader } from '../components/ui/PageHeader'
import { merchData } from '../data'
import { merchImagesByProduct } from '../data/merch-images'
import { PayPalButtons } from '@paypal/react-paypal-js'
import type { MerchProduct } from '../data/types'

type Product = MerchProduct

/** Merch and donations page with product cards and modal previews. */
const MerchDonations: FC = () => {
  const products = merchData.products
  const [activeProduct, setActiveProduct] = useState<Product | null>(null)
  const [qty, setQty] = useState(1)
  const [size, setSize] = useState('M')
  const [activeImage, setActiveImage] = useState<string | null>(null)
  const [isZoomOpen, setIsZoomOpen] = useState(false)
  const [toastMessage, setToastMessage] = useState<string | null>(null)
  const dispatch = useAppDispatch()
  const cartCount = useAppSelector((state) =>
    state.cart.items.reduce((sum, item) => sum + item.quantity, 0),
  )

  const [donationAmount, setDonationAmount] = useState<number | null>(null)
  const [customAmount, setCustomAmount] = useState('')
  const [donationThanks, setDonationThanks] = useState(false)

  const effectiveDonation =
    donationAmount ?? (customAmount && Number(customAmount) > 0 ? Number(customAmount) : null)

  const isApparel = useMemo(() => activeProduct?.category === 'apparel', [activeProduct])
  const galleryImages = useMemo(() => {
    if (!activeProduct) return []
    return merchImagesByProduct[activeProduct.id] ?? []
  }, [activeProduct])

  useEffect(() => {
    document.body.style.overflow = activeProduct ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [activeProduct])

  const closeModal = () => {
    setActiveProduct(null)
    setQty(1)
    setSize('M')
    setActiveImage(null)
    setIsZoomOpen(false)
  }

  const addToCart = (product: Product, quantity = 1, selectedSize?: string) => {
    const sizeKey = product.category === 'apparel' ? selectedSize : undefined
    dispatch(
      addItem({
        item: {
          id: product.id,
          name: product.name,
          price: product.price,
          size: sizeKey,
          freeShipping: product.freeShipping,
        },
        quantity,
      }),
    )
    setToastMessage('Added to cart')
    window.setTimeout(() => setToastMessage(null), 2200)
  }

  return (
    <div className='space-y-12'>
      <PageHeader
        title='Merch + Donations'
        subtitle='Add merch items to your cart or donate to support Ukraine.'
      />

      <section className='space-y-6'>
        <div className='flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between'>
          <PageTitle as='h2'>Merch</PageTitle>
          <span className='text-sm uppercase tracking-[0.3em] text-brand-blue/70'>
            Items in cart: {cartCount}
          </span>
        </div>
        <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-4'>
          {products.map((product) => (
            <Card
              key={product.id}
              className='flex h-full flex-col cursor-pointer'
              onClick={() => {
                const productImages = merchImagesByProduct[product.id] ?? []
                setActiveProduct(product)
                setActiveImage(productImages[0] ?? null)
              }}
            >
              <div className='overflow-hidden rounded-xl bg-brand-smoky/80 h-40 shadow-[0_12px_30px_rgba(0,0,0,0.35)] ring-1 ring-black/20'>
                {(() => {
                  const productImages = merchImagesByProduct[product.id] ?? []
                  const mainImage = productImages[0]

                  if (!mainImage) {
                    return (
                      <div className='flex h-full w-full items-center justify-center text-sm uppercase tracking-[0.3em] text-brand-azure/60'>
                        Image coming soon
                      </div>
                    )
                  }

                  return (
                    <img
                      src={mainImage}
                      alt={product.name}
                      className='h-full w-full object-cover'
                    />
                  )
                })()}
              </div>
              <p className='mt-4 text-lg font-semibold text-brand-blue'>{product.name}</p>
              <p className='mt-2 text-brand-blue'>{product.price}</p>
              <div className='mt-auto pt-4'>
                <Button
                  className='w-full'
                  variant='primary'
                  size='sm'
                  onClick={(event) => {
                    event.stopPropagation()
                    addToCart(product, 1, size)
                  }}
                  type='button'
                >
                  Add to Cart
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </section>

      <Panel className='rounded-xl p-6 sm:px-6 sm:py-6'>
        <div className='space-y-5'>
          <div>
            <PageTitle as='h2'>Donations</PageTitle>
            <p className='text-sm text-brand-azure/80'>
              Don&apos;t want the merch but do want to support heavy metal and Ukraine? Donate:
            </p>
          </div>
          <div className='flex flex-wrap gap-2'>
            {[5, 10, 20, 50].map((amount) => (
              <Button
                key={amount}
                variant={donationAmount === amount && !customAmount ? 'primary' : 'secondary'}
                size='sm'
                type='button'
                onClick={() => {
                  setDonationAmount(amount)
                  setCustomAmount('')
                  setDonationThanks(false)
                }}
              >
                £{amount}
              </Button>
            ))}
          </div>
          <div className='flex items-center gap-3'>
            <span className='text-sm text-brand-azure/70'>Or enter amount:</span>
            <div className='relative'>
              <span className='absolute left-3 top-1/2 -translate-y-1/2 text-sm text-brand-azure/60'>
                £
              </span>
              <input
                type='number'
                min='1'
                step='1'
                className='w-28 rounded-xl bg-brand-smoky py-2 pl-7 pr-4 text-sm text-brand-azure ring-1 ring-black/30'
                value={customAmount}
                placeholder='0.00'
                onChange={(event) => {
                  setCustomAmount(event.target.value)
                  setDonationAmount(null)
                  setDonationThanks(false)
                }}
              />
            </div>
          </div>
          {donationThanks ? (
            <p className='text-sm font-semibold text-brand-gold'>
              Thank you for your donation! Slava Ukraini! 🇺🇦
            </p>
          ) : effectiveDonation ? (
            <div className='max-w-xs'>
              <PayPalButtons
                style={{ color: 'gold', shape: 'rect', label: 'donate', height: 40 }}
                createOrder={(_data, actions) =>
                  actions.order.create({
                    intent: 'CAPTURE',
                    purchase_units: [
                      { amount: { value: effectiveDonation.toFixed(2), currency_code: 'GBP' } },
                    ],
                  })
                }
                onApprove={async (_data, actions) => {
                  await actions.order!.capture()
                  setDonationThanks(true)
                }}
              />
            </div>
          ) : null}
        </div>
      </Panel>

      {activeProduct && (
        <div
          className='fixed inset-0 z-50 flex items-center justify-center bg-black px-4 py-6'
          onClick={closeModal}
          role='dialog'
          aria-modal='true'
        >
          <div
            className='w-full max-w-lg rounded-xl bg-brand-smoky p-6 text-brand-azure shadow-[0_22px_50px_rgba(0,0,0,0.45)] ring-1 ring-black/25'
            onClick={(event) => event.stopPropagation()}
          >
            <div className='flex items-start justify-between gap-4'>
              <div>
                <p className='text-sm uppercase tracking-[0.3em] text-brand-blue/70'>Merch Item</p>
                <h3 className='font-display text-2xl text-brand-blue'>{activeProduct.name}</h3>
              </div>
              <button
                type='button'
                className='bg-transparent p-1 text-xl leading-none text-white/70 hover:text-white'
                onClick={closeModal}
                aria-label='Close'
              >
                ×
              </button>
            </div>

            <div
              className={`mt-5 grid gap-4 ${galleryImages.length > 1 ? 'sm:grid-cols-[1.2fr_0.8fr]' : ''}`}
            >
              <button
                type='button'
                className='h-56 overflow-hidden rounded-xl bg-transparent p-0 ring-1 ring-black/20'
                onClick={() => activeImage && setIsZoomOpen(true)}
              >
                {activeImage ? (
                  <img
                    src={activeImage}
                    alt={activeProduct.name}
                    className='h-full w-full object-cover'
                  />
                ) : (
                  <div className='flex h-full w-full items-center justify-center text-sm uppercase tracking-[0.3em] text-brand-azure/60'>
                    Images coming soon
                  </div>
                )}
              </button>
              {galleryImages.length > 1 && (
                <div className='grid gap-3'>
                  {galleryImages.map((image, index) => (
                    <button
                      key={`${activeProduct.name}-thumb-${index}`}
                      type='button'
                      className={`h-24 overflow-hidden rounded-xl bg-transparent p-0 ${
                        activeImage === image ? 'ring-2 ring-brand-gold' : 'ring-1 ring-black/20'
                      }`}
                      onClick={() => {
                        setActiveImage(image)
                        setIsZoomOpen(true)
                      }}
                    >
                      <img src={image} alt='' className='h-full w-full object-cover' />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className='mt-6 grid gap-4 sm:grid-cols-2'>
              <div>
                <p className='text-sm uppercase tracking-[0.3em] text-brand-blue/70'>Price</p>
                <p className='mt-2 text-2xl font-semibold text-brand-blue'>{activeProduct.price}</p>
              </div>
              {isApparel && (
                <div>
                  <p className='text-sm uppercase tracking-[0.3em] text-brand-blue/70'>Size</p>
                  <div className='mt-2 flex flex-wrap gap-2'>
                    {['S', 'M', 'L', 'XL', 'XXL'].map((label) => (
                      <Button
                        key={label}
                        className={`px-3 py-1 text-sm ${
                          size === label
                            ? 'ring-2 ring-brand-gold text-brand-blue'
                            : 'ring-1 ring-black/20 text-brand-azure/80 hover:text-brand-gold'
                        }`}
                        variant='secondary'
                        size='sm'
                        onClick={() => setSize(label)}
                      >
                        {label}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className='mt-6 flex flex-wrap items-center justify-between gap-4'>
              <div className='flex items-center gap-3'>
                <Button
                  className='px-3 py-1 text-sm text-brand-azure/80'
                  variant='secondary'
                  size='sm'
                  onClick={() => setQty((value) => Math.max(1, value - 1))}
                >
                  -
                </Button>
                <span className='min-w-[2rem] text-center text-sm text-brand-azure'>{qty}</span>
                <Button
                  className='px-3 py-1 text-sm text-brand-azure/80'
                  variant='secondary'
                  size='sm'
                  onClick={() => setQty((value) => value + 1)}
                >
                  +
                </Button>
              </div>
              <Button
                className='px-6 py-3 text-sm'
                variant='primary'
                size='md'
                onClick={() => {
                  if (activeProduct) {
                    addToCart(activeProduct, qty, size)
                    closeModal()
                  }
                }}
              >
                Add to Cart
              </Button>
            </div>

            {isZoomOpen &&
              activeImage &&
              (() => {
                const zoomIndex = galleryImages.indexOf(activeImage)
                return (
                  <div
                    className='fixed inset-0 z-50 flex items-center justify-center bg-black/90 px-4 py-6'
                    onClick={() => setIsZoomOpen(false)}
                    role='dialog'
                    aria-modal='true'
                  >
                    <div
                      className='relative w-full max-w-3xl max-h-[85vh] overflow-hidden rounded-xl bg-brand-smoky shadow-[0_22px_50px_rgba(0,0,0,0.45)] ring-1 ring-black/25'
                      onClick={(event) => event.stopPropagation()}
                    >
                      <img
                        src={activeImage}
                        alt={activeProduct.name}
                        className='max-h-[85vh] w-full object-contain'
                      />
                      <button
                        type='button'
                        className='absolute top-3 right-3 p-1 text-xl leading-none text-white/70 hover:text-white'
                        onClick={() => setIsZoomOpen(false)}
                        aria-label='Close'
                      >
                        ×
                      </button>
                      {galleryImages.length > 1 && (
                        <>
                          <button
                            type='button'
                            className='absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-black/50 px-3 py-2 text-2xl font-bold leading-none text-white hover:bg-black/80'
                            onClick={() =>
                              setActiveImage(
                                galleryImages[
                                  (zoomIndex - 1 + galleryImages.length) % galleryImages.length
                                ],
                              )
                            }
                            aria-label='Previous image'
                          >
                            ←
                          </button>
                          <button
                            type='button'
                            className='absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-black/50 px-3 py-2 text-2xl font-bold leading-none text-white hover:bg-black/80'
                            onClick={() =>
                              setActiveImage(galleryImages[(zoomIndex + 1) % galleryImages.length])
                            }
                            aria-label='Next image'
                          >
                            →
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )
              })()}
          </div>
        </div>
      )}

      {toastMessage && (
        <div
          className='fixed top-24 left-1/2 z-50 -translate-x-1/2 rounded-xl px-8 py-4 text-sm font-semibold uppercase tracking-[0.3em] shadow-[0_18px_40px_rgba(0,0,0,0.55)] whitespace-nowrap'
          style={{ backgroundColor: '#F8D309', color: '#191102' }}
        >
          {toastMessage}
        </div>
      )}
    </div>
  )
}

export default MerchDonations
